import React, { useEffect, useMemo, useState } from 'react';
import { X, UserPlus, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import userService from '../../services/userService';
import MultiSelectDropdown from '../MultiSelectDropdown/MultiSelectDropdown';
import './AssignResponsablesToCentreDialog.css';

const AssignResponsablesToCentreDialog = ({ isOpen, onClose, centre, onUpdated }) => {
  const { t } = useTranslation();
  const [responsables, setResponsables] = useState([]);
  const [selectedResponsableIds, setSelectedResponsableIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResponsables = async () => {
      if (!isOpen || !centre) return;
      try {
        setLoading(true);
        setError(null);
        const response = await userService.getResponsables();
        const data = Array.isArray(response) ? response : response?.results || [];
        setResponsables(data);

        const preselected = data
          .filter((r) => (r.centres || []).some((cId) => String(cId) === String(centre.id)))
          .map((r) => String(r.id));
        setSelectedResponsableIds(preselected);
      } catch (err) {
        console.error('Error fetching responsables:', err);
        setError(t('centres.assignDialog.errors.loadResponsables'));
        setResponsables([]);
        setSelectedResponsableIds([]);
      } finally {
        setLoading(false);
      }
    };

    fetchResponsables();
  }, [isOpen, centre]);

  const options = useMemo(() => {
    return (responsables || []).map((r) => ({
      value: String(r.id),
      label: `${r.first_name || ''} ${r.last_name || ''}`.trim() + (r.email ? ` (${r.email})` : ''),
    }));
  }, [responsables]);

  if (!isOpen || !centre) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const selectedSet = new Set(selectedResponsableIds.map(String));

      const updates = (responsables || []).map(async (r) => {
        const currentCentreIds = (r.centres || []).map(String);
        const isSelected = selectedSet.has(String(r.id));

        const nextCentreIds = isSelected
          ? Array.from(new Set([...currentCentreIds, String(centre.id)]))
          : currentCentreIds.filter((id) => id !== String(centre.id));

        const nextSameAsCurrent =
          nextCentreIds.length === currentCentreIds.length &&
          nextCentreIds.every((id) => currentCentreIds.includes(id));

        if (nextSameAsCurrent) return;

        await userService.assignCentresToResponsable(r.id, nextCentreIds);
      });

      await Promise.all(updates);
      onUpdated();
      onClose();
    } catch (err) {
      console.error('Error assigning responsables:', err);
      setError(err.response?.data?.detail || t('centres.assignDialog.errors.assign'));
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  return (
    <div className="dialog-overlay ui-dialog-overlay" onClick={handleOverlayClick}>
      <div className="dialog-container ui-dialog-container assign-responsables-dialog">
        <div className="dialog-header ui-dialog-header">
          <div className="dialog-title ui-dialog-title">
            <UserPlus size={24} />
            <h2>{t('centres.assignDialog.title', { name: centre.name })}</h2>
          </div>
          <button className="dialog-close" onClick={onClose} disabled={loading}>
            <X size={20} />
          </button>
        </div>

        {error && <div className="dialog-error">{error}</div>}

        <form className="dialog-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <MultiSelectDropdown
              label={t('centres.assignDialog.form.responsablesOptional')}
              icon={UserPlus}
              options={options}
              selectedValues={selectedResponsableIds}
              onChange={setSelectedResponsableIds}
              placeholder={t('centres.assignDialog.form.responsablesPlaceholder')}
            />
          </div>

          <div className="dialog-actions ui-dialog-actions">
            <button
              type="button"
              className="btn-secondary ui-btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary ui-btn-primary"
              disabled={loading}
            >
              {loading ? t('common.saving') : (
                <>
                  <Save size={18} />
                  {t('common.save')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignResponsablesToCentreDialog;
